import { httpClient } from "./client";
import type { FeedPage, HomeFeedPage, Schema } from "../types/generated";

export type FeedRequest =
  | { scope: "friends" | "communities"; limit?: number }
  | { scope: "community"; communityId: string; limit?: number }
  | { scope: "user"; userId: string; limit?: number }
  | { scope: "discovery"; limit?: number };

export interface FeedReadOptions {
  signal?: AbortSignal;
}
export type FeedFreshnessTelemetryBatch = Schema["FeedFreshnessTelemetryBatch"];

const PERSONAL_SCOPES = new Set(["friends", "communities"]);
const HOME_FEED_PATH = "/feed/home";

function assertExactKeys(
  request: Record<string, unknown>,
  allowed: readonly string[],
): void {
  if (Object.keys(request).some((key) => !allowed.includes(key))) {
    throw new TypeError("Invalid Feed request");
  }
}

function assertLimit(limit: unknown): void {
  if (
    limit !== undefined &&
    (!Number.isInteger(limit) ||
      (limit as number) < 1 ||
      (limit as number) > 50)
  ) {
    throw new TypeError("Invalid Feed limit");
  }
}

function assertFeedRequest(request: FeedRequest): void {
  if (!request || typeof request !== "object" || Array.isArray(request)) {
    throw new TypeError("Invalid Feed request");
  }
  const candidate = request as unknown as Record<string, unknown>;
  assertLimit(candidate.limit);
  if (PERSONAL_SCOPES.has(String(candidate.scope))) {
    assertExactKeys(candidate, ["scope", "limit"]);
    return;
  }
  if (candidate.scope === "community") {
    assertExactKeys(candidate, ["scope", "communityId", "limit"]);
    if (typeof candidate.communityId !== "string" || !candidate.communityId) {
      throw new TypeError("A community Feed requires communityId");
    }
    return;
  }
  if (candidate.scope === "user") {
    assertExactKeys(candidate, ["scope", "userId", "limit"]);
    if (typeof candidate.userId !== "string" || !candidate.userId) {
      throw new TypeError("A user Feed requires userId");
    }
    return;
  }
  if (candidate.scope === "discovery") {
    assertExactKeys(candidate, ["scope", "limit"]);
    return;
  }
  throw new TypeError("Invalid Feed scope");
}

function assertFeedPage(value: unknown): FeedPage {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Invalid Feed response");
  }
  const page = value as Partial<FeedPage>;
  if (
    !Array.isArray(page.items) ||
    page.items.some(
      (item) =>
        !item ||
        typeof item !== "object" ||
        typeof (item as { post_id?: unknown }).post_id !== "string",
    ) ||
    typeof page.has_more !== "boolean" ||
    (page.next_cursor != null && typeof page.next_cursor !== "string") ||
    (page.has_more &&
      (typeof page.next_cursor !== "string" || page.next_cursor.length === 0))
  ) {
    throw new TypeError("Invalid Feed response");
  }
  return {
    items: page.items,
    next_cursor: page.next_cursor ?? null,
    has_more: page.has_more,
  };
}

function hasExactKeys(value: Record<string, unknown>, keys: string[]): boolean {
  return (
    Object.keys(value).length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key))
  );
}

function assertHomeFeedPage(value: unknown): HomeFeedPage {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Invalid Home Feed response");
  }
  const page = value as Record<string, unknown>;
  if (
    !hasExactKeys(page, [
      "items",
      "next_cursor",
      "has_more",
      "partial_sources",
    ]) ||
    !Array.isArray(page.items) ||
    typeof page.has_more !== "boolean" ||
    (page.next_cursor !== null && typeof page.next_cursor !== "string") ||
    (page.has_more && !page.next_cursor) ||
    !Array.isArray(page.partial_sources) ||
    page.partial_sources.some((source) => source !== "activity")
  ) {
    throw new TypeError("Invalid Home Feed response");
  }
  for (const item of page.items) {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      throw new TypeError("Invalid Home Feed response");
    }
    const candidate = item as Record<string, unknown>;
    if (
      candidate.kind === "post" &&
      hasExactKeys(candidate, ["kind", "occurred_at", "post"]) &&
      typeof candidate.occurred_at === "string" &&
      candidate.post &&
      typeof candidate.post === "object" &&
      typeof (candidate.post as { post_id?: unknown }).post_id === "string"
    ) {
      continue;
    }
    const isRankedMatch = candidate.kind === "ranked_match_completed";
    if (
      !isRankedMatch ||
      !hasExactKeys(candidate, ["kind", "occurred_at", "activity"]) ||
      typeof candidate.occurred_at !== "string" ||
      !candidate.activity ||
      typeof candidate.activity !== "object" ||
      Array.isArray(candidate.activity)
    ) {
      throw new TypeError("Invalid Home Feed response");
    }
    const activity = candidate.activity as Record<string, unknown>;
    const actor = activity.actor as Record<string, unknown> | undefined;
    const action = activity.action as Record<string, unknown> | undefined;
    if (
      !hasExactKeys(activity, [
        "activity_id",
        "occurred_at",
        "actor",
        "mode",
        "outcome",
        "action",
      ]) ||
      typeof activity.activity_id !== "string" ||
      typeof activity.occurred_at !== "string" ||
      !actor ||
      !hasExactKeys(actor, ["user_id", "display_name", "avatar_url"]) ||
      typeof actor.user_id !== "string" ||
      typeof actor.display_name !== "string" ||
      (actor.avatar_url !== null && typeof actor.avatar_url !== "string") ||
      !["singles", "doubles"].includes(String(activity.mode)) ||
      !["won", "lost"].includes(String(activity.outcome)) ||
      !action ||
      !hasExactKeys(action, ["kind", "user_id"]) ||
      action.kind !== "player_profile" ||
      typeof action.user_id !== "string" ||
      action.user_id !== actor.user_id
    ) {
      throw new TypeError("Invalid Home Feed response");
    }
  }
  return value as HomeFeedPage;
}

function requestPath(request: FeedRequest): string {
  if (PERSONAL_SCOPES.has(request.scope)) return "/feed/posts";
  if (request.scope === "community") {
    return `/feed/communities/${encodeURIComponent(request.communityId)}/posts`;
  }
  if (request.scope === "user") {
    return `/feed/users/${encodeURIComponent(request.userId)}/posts`;
  }
  return "/feed/discovery";
}

export const feedApi = {
  async recordFreshnessTelemetry(
    batch: FeedFreshnessTelemetryBatch,
  ): Promise<void> {
    await httpClient.post<void>("/feed/freshness/telemetry", batch, {
      redirectOnUnauthorized: false,
    });
  },
  async getHomePage(
    request: { limit?: number } = {},
    cursor: string | null = null,
    options: FeedReadOptions = {},
  ): Promise<HomeFeedPage> {
    assertExactKeys(request, ["limit"]);
    assertLimit(request.limit);
    if (cursor !== null && (typeof cursor !== "string" || !cursor)) {
      throw new TypeError("Invalid Feed cursor");
    }
    const params = new URLSearchParams();
    params.set("limit", String(request.limit ?? 20));
    if (cursor !== null) params.set("cursor", cursor);
    return assertHomeFeedPage(
      await httpClient.get<unknown>(`${HOME_FEED_PATH}?${params.toString()}`, {
        signal: options.signal,
      }),
    );
  },
  async getPage(
    request: FeedRequest,
    cursor: string | null = null,
    options: FeedReadOptions = {},
  ): Promise<FeedPage> {
    assertFeedRequest(request);
    if (cursor !== null && (typeof cursor !== "string" || !cursor)) {
      throw new TypeError("Invalid Feed cursor");
    }
    const params = new URLSearchParams();
    params.set("limit", String(request.limit ?? 20));
    if (PERSONAL_SCOPES.has(request.scope)) params.set("scope", request.scope);
    if (cursor !== null) params.set("cursor", cursor);
    const page = await httpClient.get<unknown>(
      `${requestPath(request)}?${params.toString()}`,
      { signal: options.signal },
    );
    return assertFeedPage(page);
  },
};
